import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { TreePine, Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API_URL } from '../../lib/api';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading, success, error, no-token
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (!token) {
        setStatus('no-token');
        setMessage('No subscription token provided.');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/unsubscribe/${token}`, {
          method: 'POST'
        });

        if (response.ok) {
          setStatus('success');
          setMessage('You have been successfully unsubscribed from our newsletter.');
        } else {
          const data = await response.json();
          setStatus('error');
          setMessage(data.detail || 'Unable to process your request.');
        }
      } catch (err) {
        setStatus('error');
        setMessage('An error occurred. Please try again later.');
      }
    };

    handleUnsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-md mx-auto px-4 py-16">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center">
            {/* Logo */}
            <div className="w-16 h-16 mx-auto mb-6 bg-emerald-100 rounded-2xl flex items-center justify-center">
              <TreePine className="w-8 h-8 text-emerald-600" />
            </div>

            {status === 'loading' && (
              <>
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-emerald-500" />
                <h1 className="text-2xl font-serif font-bold text-slate-800 mb-2">
                  Processing...
                </h1>
                <p className="text-slate-600">
                  Please wait while we update your subscription preferences.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                <h1 className="text-2xl font-serif font-bold text-slate-800 mb-2">
                  Unsubscribed
                </h1>
                <p className="text-slate-600 mb-6">
                  {message}
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  We're sorry to see you go. If you change your mind, you can always subscribe again from our website.
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h1 className="text-2xl font-serif font-bold text-slate-800 mb-2">
                  Oops!
                </h1>
                <p className="text-slate-600 mb-6">
                  {message}
                </p>
              </>
            )}

            {status === 'no-token' && (
              <>
                <Mail className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h1 className="text-2xl font-serif font-bold text-slate-800 mb-2">
                  Manage Subscription
                </h1>
                <p className="text-slate-600 mb-6">
                  To unsubscribe, please use the link in one of our emails, or contact us directly.
                </p>
              </>
            )}

            <Link to="/">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8">
                Return to Home
              </Button>
            </Link>
          </div>

          {/* Contact info */}
          <div className="text-center mt-8">
            <p className="text-sm text-slate-500">
              Need help? Contact us at{' '}
              <a href="mailto:info@creassion.org" className="text-emerald-600 hover:underline">
                info@creassion.org
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UnsubscribePage;
